import { EFinancingCompany } from '@if/core/types'
import { keysFactory as merchantKeysFactory } from '@src/data/api/merchants-api/merchants-api'
import { MerchantDashboardUser } from '@src/data/types/MerchantDashboardUserSchema'
import { reportErrorToConsole } from '@src/services/error-logger'
import { MutationFunction, QueryFunctionContext, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getApiClient } from '../api-client'

export interface JwtToken {
  jwt: string
}

export async function signIn(data: JwtToken): Promise<JwtToken> {
  const cApiClient = getApiClient()
  return cApiClient
    .post('/GenerateJwtToken', data)
    .then((response) => {
      return response.data as JwtToken
    })
    .catch((error) => {
      reportErrorToConsole(error)
      return {} as JwtToken
    })
}

export interface TestProfile {
  username: string
  roles: string[]
}

export interface MockLogin {
  username: string
  companyIds: EFinancingCompany[]
}

export async function mockLogin(data: MockLogin): Promise<JwtToken> {
  const cApiClient = getApiClient()
  const response = await cApiClient.post('/MockLogin', data)
  return response.data as JwtToken
}

const SCOPE = 'user-profile'
const LIST = 'list'
const DETAIL = 'detail'
const TEST_PROFILES = 'test-profiles'

const keysFactory = {
  all: () => [{ scope: SCOPE }] as const,
  allLists: () => [{ scope: SCOPE, entity: LIST }] as const,
  list: (id: string) => [{ scope: SCOPE, entity: LIST, id }] as const,
  allDetails: () => [{ scope: SCOPE, entity: DETAIL }] as const,
  detail: (id: string) => [{ scope: SCOPE, entity: DETAIL, id }] as const,
  searchUsers: (searchTerm: string) => [{ scope: SCOPE, entity: LIST, searchTerm }] as const,
  testProfiles: () => [{ scope: SCOPE, entity: TEST_PROFILES }] as const,
}

const getTestProfiles = async () => {
  const cApiClient = getApiClient()
  const response = await cApiClient.get('/MockLogin')
  return response.data as TestProfile[]
}

export function useTestProfiles(enabled: boolean): [TestProfile[], boolean, Error | null] {
  const { data, isFetching, error } = useQuery({
    queryKey: keysFactory.testProfiles(),
    queryFn: getTestProfiles,
    initialData: [],
    enabled,
  })

  return [data, isFetching, error]
}

const createUser: MutationFunction<MerchantDashboardUser, MerchantDashboardUser> = async (
  user: MerchantDashboardUser,
) => {
  const cApiClient = getApiClient()
  const response = await cApiClient.post('/Users', user)
  return response.data as MerchantDashboardUser
}

export function useCreateUser(): [
  MutationFunction<MerchantDashboardUser, MerchantDashboardUser>,
  boolean,
  () => void,
  Error | null,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, reset, error } = useMutation({
    mutationFn: createUser,
    onSuccess: (data) => {
      if (data.merchantId) {
        queryClient.setQueryData<MerchantDashboardUser[]>(
          merchantKeysFactory.merchantUsers(data.merchantId),
          (previousUsers) => (previousUsers ? [...previousUsers, data] : [data]),
        )
      }
    },
  })

  return [mutateAsync, isPending, reset, error]
}

const updateUser: MutationFunction<MerchantDashboardUser, MerchantDashboardUser> = async (
  user: MerchantDashboardUser,
) => {
  const cApiClient = getApiClient()
  const response = await cApiClient.put('/Users', user)
  return response.data as MerchantDashboardUser
}

export function useUpdateUser(): [MutationFunction<MerchantDashboardUser, MerchantDashboardUser>, boolean, () => void] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, reset } = useMutation({
    mutationFn: updateUser,
    onSuccess: (data) => {
      queryClient.setQueryData(keysFactory.detail(data.email), data)
      if (data.merchantId) {
        queryClient.setQueryData<MerchantDashboardUser[]>(
          merchantKeysFactory.merchantUsers(data.merchantId),
          (previousUsers) => previousUsers?.map((user) => (user.email === data.email ? data : user)),
        )
      }
    },
  })

  return [mutateAsync, isPending, reset]
}

const resetPassword: MutationFunction<string, string> = async (email: string) => {
  const cApiClient = getApiClient()
  const response = await cApiClient.post(`/Users/ResetPassword?email=${encodeURIComponent(email)}`)
  return response.data as string
}

export function useResetPassword(): [MutationFunction<string, string>, boolean, () => void] {
  const { mutateAsync, isPending, reset } = useMutation({
    mutationFn: resetPassword,
  })

  return [mutateAsync, isPending, reset]
}

const getUser = async ({ queryKey: [{ id }] }: QueryFunctionContext<ReturnType<(typeof keysFactory)['detail']>>) => {
  const cApiClient = getApiClient()
  const response = await cApiClient.get(`/Users/GetByEmail?email=${encodeURIComponent(id)}`)
  return response.data as MerchantDashboardUser
}

export function useGetUser(id: string): [MerchantDashboardUser | null, boolean] {
  const { data, isFetching } = useQuery({
    queryKey: keysFactory.detail(id),
    queryFn: getUser,
    enabled: !!id,
  })

  return [data ?? null, isFetching]
}

const searchUsers = async ({
  queryKey: [{ searchTerm }],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['searchUsers']>>) => {
  const apiClient = getApiClient()
  const response = await apiClient.get(`/Users/Search?q=${encodeURIComponent(searchTerm)}`)
  return response.data as MerchantDashboardUser[]
}

export function useSearchUsers(searchTerm: string): [MerchantDashboardUser[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: keysFactory.searchUsers(searchTerm),
    queryFn: searchUsers,
    initialData: [],
    enabled: searchTerm.length > 2,
  })

  return [data, isFetching]
}
